import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { login, signup } from "../lib/auth-actions";
import { validateEmail, validatePassword } from "../lib/validators";

type Mode = "login" | "signup";

export function AuthPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const emailErr = validateEmail(email);
    const pwErr = validatePassword(password);
    if (emailErr || pwErr) {
      setError(emailErr ?? pwErr);
      return;
    }
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/");
      } else {
        await signup(email, password);
        navigate("/verify-email");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "요청을 처리하지 못했습니다");
    } finally {
      setBusy(false);
    }
  }

  const tab = (m: Mode, label: string) => (
    <button
      type="button"
      onClick={() => {
        setMode(m);
        setError(null);
      }}
      className={
        mode === m
          ? "flex-1 rounded-lg py-2 text-sm font-medium btn-neon"
          : "flex-1 rounded-lg py-2 text-sm font-medium text-slate-400 transition hover:text-slate-200"
      }
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto mt-10 w-full max-w-sm">
      <div className="mb-8 text-center fade-up">
        <p className="mb-2 font-mono text-xs tracking-[0.3em] text-cyan-400/70">{"// CLASSFIRE"}</p>
        <h1 className="neon-text text-3xl font-bold">PROJECT SHARE</h1>
        <p className="mt-2 text-sm text-slate-400">로그인하고 프로젝트를 공유하세요</p>
      </div>

      <div className="glass neon-border rounded-2xl p-7 fade-up">
        <div className="mb-5 flex gap-2">
          {tab("login", "로그인")}
          {tab("signup", "회원가입")}
        </div>

        <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
          <input
            type="email"
            placeholder="이메일"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-cyber rounded-lg px-3 py-2.5"
          />
          <input
            type="password"
            placeholder="비밀번호 (6자 이상)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-cyber rounded-lg px-3 py-2.5"
          />
          {error && <p className="text-sm text-fuchsia-400">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="btn-neon mt-1 rounded-lg py-2.5 font-semibold tracking-wide"
          >
            {mode === "login" ? "로그인 →" : "회원가입 →"}
          </button>
        </form>

        {mode === "login" && (
          <p className="mt-4 text-center text-xs text-slate-500">
            <Link to="/forgot-password" className="transition hover:text-cyan-300">
              비밀번호를 잊으셨나요?
            </Link>
          </p>
        )}
      </div>

      <p className="mt-6 text-center font-mono text-xs text-slate-600">
        처음이라면 회원가입을 눌러주세요
      </p>
    </div>
  );
}
